import { useAuthStore } from "@/store/auth"
import { PublicConnectorDetails } from "@/store/newPublicConnector"
import {
  AnalyticsResponseType,
  TransactionsAnalyticsData
} from "@/types/Analytics"
import { ConfigType, ConnectorType, DataConnectorsType } from "@/types/DataConnectorsType"
import { ResponseType } from "@/types/SignInResponseType"
import { StatisticType } from "@/types/StatisticType"
import { TemplatesType } from "@/types/TemplatesType"
import { UserType, RoleEnum } from "@/types/UserType"
import { notification } from "antd"
import ky, { NormalizedOptions, HTTPError } from "ky"
import { SERVICE_PATHS, SERVICE_PORTS } from '../constants'
import { freshStaffToken, getStaffToken, isStaffAdminHost, isStaffAuthPath, signInPath } from '@/adminHost'
import { getActiveCompanyId } from '@/shared/activeCompany'
import { sessionUserIsActive } from '@/shared/sessionActivity'
import { describeSignInFailure, SignInFailure } from '@/utils/signInError'

// API base: prefer VITE_WEBSITE_API_BASE_URL, fall back to VITE_API_BASE_URL / VITE_API_URL for backward compatibility
const isDevelopment = import.meta.env.DEV || process.env.NODE_ENV === 'development';
export const BASE_URL = import.meta.env.VITE_WEBSITE_API_BASE_URL ?? import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL ?? (isDevelopment ? `http://localhost:${SERVICE_PORTS.API}` : window.location.origin);
// Auth goes through API in prod/staging; fallback to BASE_URL so login works if only API URL is set (e.g. staging)
export const AUTH_URL = import.meta.env.VITE_WEBSITE_AUTH_URL ?? import.meta.env.VITE_AUTH_URL ?? (isDevelopment ? `http://localhost:${SERVICE_PORTS.AUTH}` : (BASE_URL || window.location.origin));

// Helper: determine if current path is a public (unauthenticated) route
const isPublicRoute = (pathname: string): boolean => {
  if (isStaffAuthPath(pathname)) return true;
  const publicRoutes = [
    '/sign-in',
    '/sign-up',
    '/confirm-account',
    '/forgot-password',
    '/reset-password',
    '/mfa-verify',
    '/oauth-success',
    '/oauth-error',
    '/privacy-policy',
    '/terms-service'
  ];
  return publicRoutes.some(route => pathname.startsWith(route));
};

const redirectToSignIn = () => {
  if (typeof window === 'undefined') return;
  if (isPublicRoute(window.location.pathname)) return;
  if (isStaffAdminHost() && getStaffToken()) return;
  window.location.href = signInPath();
};

function describeClientComputer(): string {
  if (typeof navigator === 'undefined') return '';
  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string; mobile?: boolean } }).userAgentData;
  const platform = uaData?.platform || navigator.platform || '';
  const ua = navigator.userAgent || '';
  const kind = uaData?.mobile || /Mobile|iPhone|Android/i.test(ua)
    ? 'Mobile'
    : /iPad|Tablet/i.test(ua)
      ? 'Tablet'
      : 'Desktop';
  return [kind, platform].filter(Boolean).join(' · ').slice(0, 120);
}

function signInClientHeaders(realIp?: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Origin': window.location.origin,
  };
  if (realIp && realIp !== 'unknown') {
    headers['X-Client-IP'] = realIp;
  }
  const computer = describeClientComputer();
  if (computer) headers['X-Client-Computer'] = computer;
  return headers;
}

// Store client's real IP address
let clientRealIp: string | null = null;

// Fetch client's real IP address on app initialization
export const fetchClientIp = async (): Promise<string> => {
  if (clientRealIp) return clientRealIp;
  
  try {
    // Try multiple IP detection services for reliability
    const services = [
      'https://api.ipify.org?format=json',
      'https://api.my-ip.io/ip.json',
      'https://ipapi.co/json/'
    ];
    
    for (const service of services) {
      try {
        const response = await fetch(service, { timeout: 3000 } as any);
        const data = await response.json();
        clientRealIp = data.ip || data.address || data.query;
        if (clientRealIp) {
          console.log('[IP Detection] Client IP:', clientRealIp);
          return clientRealIp;
        }
      } catch (err) {
        console.warn(`[IP Detection] Failed to fetch from ${service}:`, err);
        continue;
      }
    }
  } catch (error) {
    console.error('[IP Detection] Failed to detect client IP:', error);
  }
  
  return 'unknown';
};

// Initialize IP detection
fetchClientIp();

// Same env chain as BASE_URL for users service (backward compat: VITE_API_BASE_URL, VITE_API_URL)
const USERS_BASE_URL = (import.meta.env.VITE_WEBSITE_API_BASE_URL ?? import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL) ?? (isDevelopment ? `http://localhost:${SERVICE_PORTS.USERS}` : window.location.origin);

interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  user: UserType;
}

// Cache for user data to prevent unnecessary API calls
const userDataCache = {
  data: null as UserType | null,
  timestamp: 0,
  maxAge: 5 * 60 * 1000 // 5 minutes
};

export function clearUserDataCache() {
  userDataCache.data = null;
  userDataCache.timestamp = 0;
}

// Token refresh configuration
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes before expiry
const TOKEN_REFRESH_INTERVAL = 55 * 60 * 1000; // 55 minutes
let refreshPromise: Promise<RefreshTokenResponse> | null = null;

export function parseJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
}

function shouldRefreshToken(): boolean {
  const token = localStorage.getItem('accessToken');
  if (!token) return false;

  try {
    const decoded = parseJwt(token);
    if (!decoded || !decoded.exp) return false;

    const expiryTime = decoded.exp * 1000;
    const currentTime = Date.now();
    
    return currentTime >= (expiryTime - TOKEN_REFRESH_BUFFER);
  } catch (error) {
    console.error('Error checking token expiry:', error);
    return false;
  }
}

export async function refreshTokenIfNeeded(): Promise<string | null> {
  if (!shouldRefreshToken()) {
    return localStorage.getItem('accessToken');
  }

  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    try {
      const result = await refreshPromise;
      return result.accessToken;
    } catch (error) {
      console.error('Error waiting for token refresh:', error);
      return null;
    }
  }

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return null;
  }

  // Start new refresh
  refreshPromise = handleRefreshToken(refreshToken);
  try {
    const result = await refreshPromise;
    localStorage.setItem('accessToken', result.accessToken);
    localStorage.setItem('refreshToken', result.refreshToken);
    return result.accessToken;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return null;
  } finally {
    refreshPromise = null;
  }
}

const errorNotification = async (_request: Request, _options: NormalizedOptions, response: Response) => {
  if (!response.ok) {
    const errorMessage = await response.text()
    notification.error({ placement: "topRight", message: `Error: ${errorMessage}'` })
  }
}

// Add refresh attempt tracking
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;
const BASE_DELAY = 1000; // 1 second

const getBackoffDelay = (attempt: number): number => {
  return Math.min(BASE_DELAY * Math.pow(2, attempt), 30000); // Max 30 seconds
};

const resetRefreshAttempts = () => {
  refreshAttempts = 0;
};

export const api = ky.extend({
  prefixUrl: BASE_URL,
  timeout: 60000,
  credentials: 'include' as const,
  mode: 'cors' as const,
  retry: {
    limit: 5,
    methods: ['get', 'post', 'put', 'delete'],
    statusCodes: [408, 413, 429, 500, 502, 503, 504],
    backoffLimit: 20000,
  },
  hooks: {
    beforeRequest: [
      async (request) => {
        const token = freshStaffToken() || await refreshTokenIfNeeded();
        if (!token) {
          // Only warn for protected endpoints
          if (!request.url.includes('/auth') && !request.url.includes('/registration')) {
            console.warn('No access token found for protected endpoint:', request.url);
          }
          return;
        }
        
        request.headers.set('Authorization', `Bearer ${token}`);
        request.headers.set('Origin', window.location.origin);
        if (sessionUserIsActive()) request.headers.set('X-User-Active', '1');
        const companyId = getActiveCompanyId(useAuthStore.getState().user);
        if (companyId) request.headers.set('X-Company-Id', companyId);
      },
    ],
    beforeError: [
      error => {
        const { response } = error;
        console.error('[API] Request failed:', {
          url: error.request.url,
          method: error.request.method,
          status: response?.status,
          statusText: response?.statusText,
          headers: Object.fromEntries(response?.headers || [])
        });
        return error;
      }
    ],
    afterResponse: [
      async (request, options, response) => {
        console.log('[API] Response received:', {
          url: request.url,
          method: request.method,
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers)
        });

        if (response.status === 401) {
          // Try refresh one last time
          const token = await refreshTokenIfNeeded();
          if (!token) {
            // Clear tokens and redirect to login
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            redirectToSignIn();
            return response;
          }

          // Retry the original request with new token using the api instance to preserve prefixUrl
          const retryRequest = new Request(request.url, {
            ...request,
            headers: new Headers(request.headers),
          });
          retryRequest.headers.set('Authorization', `Bearer ${token}`);
          return api(retryRequest);
        }
        return response;
      },
    ],
  },
})

let lastSessionTouch = 0;

export function touchSessionActivity() {
  if (typeof window === 'undefined') return;
  if (!localStorage.getItem('accessToken')) return;
  if (!sessionUserIsActive()) return;
  if (Date.now() - lastSessionTouch < 60_000) return;
  lastSessionTouch = Date.now();
  void api.post('api/session/touch', { json: {} }).catch(() => undefined);
}

// API Endpoints - Remove leading slashes since we're using prefixUrl
const AUTH_PATH = `${SERVICE_PATHS.USERS.substring(1)}/auth`
const ME_PATH = `${SERVICE_PATHS.USERS.substring(1)}/me`
const ALL_USERS_PATH = SERVICE_PATHS.USERS.substring(1)
const CONFIRM_USER = `${SERVICE_PATHS.USERS.substring(1)}/confirm`

const REGISTRATION_PATH = `${SERVICE_PATHS.API.substring(1)}/registration`
const REFRESH_PATH = `${SERVICE_PATHS.AUTH.substring(1)}/refresh`
const RESET_PASSWORD_PATH = `${SERVICE_PATHS.USERS.substring(1)}/reset-password`
const UPDATE_PASSWORD_PATH = `${SERVICE_PATHS.USERS.substring(1)}/update-password`

const ALL_CONNECTORS_PATH = SERVICE_PATHS.CONNECTORS.substring(1)
const CREATE_CONNECTOR_PATH = `${SERVICE_PATHS.CONNECTORS.substring(1)}/`
const UPDATE_CONNECTOR_PATH = `${SERVICE_PATHS.CONNECTORS.substring(1)}/`
const DELETE_CONNECTOR = `${SERVICE_PATHS.CONNECTORS.substring(1)}/`
const VALIDATE_CONFIG = `${SERVICE_PATHS.CONNECTORS.substring(1)}/validate-config`
const VALIDATE_DATA_MAPPING = `${SERVICE_PATHS.CONNECTORS.substring(1)}/validate-data-mapping`
const PAUSE_CONNECTOR = `${SERVICE_PATHS.CONNECTORS.substring(1)}/`
const RESUME_CONNECTOR = `${SERVICE_PATHS.CONNECTORS.substring(1)}/resume`

const ALL_CONFIGS_PATH = SERVICE_PATHS.CONFIGS.substring(1)

const GET_DASHBOARD_ANALYTICS = `${SERVICE_PATHS.API.substring(1)}/analytics/dashboard`
const GET_STATISTIC = `${SERVICE_PATHS.API.substring(1)}/analytics/statistic`
const GET_TRANSACTIONS = `${SERVICE_PATHS.API.substring(1)}/analytics`

const GET_ALL_PUBLIC_CONNECTORS = `${SERVICE_PATHS.API.substring(1)}/templates`
const CREATE_PUBLIC_CONNECTOR = `${SERVICE_PATHS.API.substring(1)}/templates`
const UPDATE_PUBLIC_CONNECTOR = `${SERVICE_PATHS.API.substring(1)}/templates/`
const DELETE_PUBLIC_CONNECTOR = `${SERVICE_PATHS.API.substring(1)}/templates/`

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

interface UserDataCache {
  data: UserType | null;
  timestamp: number;
}

// Define required fields type
type RequiredUserFields = '_id' | 'email' | 'firstName' | 'lastName';

interface RawMeResponse {
  id?: string;
  _id?: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  role?: string | { role: string };
  companyName?: string;
  workspaceName?: string;
  confirmed?: boolean;
  integrations?: any[];
  companies?: any[];
}

export async function getMe(): Promise<UserType> {
  try {
    // Check cache first
    if (userDataCache.data && Date.now() - userDataCache.timestamp < CACHE_DURATION) {
      return userDataCache.data;
    }

    // Always send the token with the request
    const token = localStorage.getItem('accessToken');
    const tokenValue = token && token.trim().startsWith('Bearer ') ? token.trim() : `Bearer ${token?.trim()}`;
    const rawData = await ky.get(`${USERS_BASE_URL}/${ME_PATH}`, {
      headers: {
        Authorization: tokenValue,
        'Content-Type': 'application/json',
      }
    }).json<RawMeResponse>();
    
    // Normalize company ID to string for storage and UserType
    const normalizeCompanyId = (v: any): string | undefined => {
      if (v == null) return undefined;
      if (typeof v === 'string') return v;
      return (v as any)?._id?.toString?.() ?? v?.toString?.();
    };
    const primaryId = normalizeCompanyId(rawData.primaryCompanyId);
    const companiesArray = Array.isArray(rawData.companies)
      ? rawData.companies.map((c: any) => normalizeCompanyId(c) ?? '').filter(Boolean)
      : undefined;

    // Map the response to our expected UserType format
    const data: UserType = {
      _id: rawData._id || rawData.id || '',
      email: rawData.email || '',
      firstName:
        rawData.firstName ||
        (rawData.name ? rawData.name.split(' ')[0] : '') ||
        (rawData.email ? rawData.email.split('@')[0] : 'User'),
      lastName:
        rawData.lastName ||
        (rawData.name ? rawData.name.split(' ').slice(1).join(' ') : '') ||
        '',
      role: typeof rawData.role === 'string'
        ? (Object.values(RoleEnum).includes(rawData.role as RoleEnum) ? rawData.role as RoleEnum : RoleEnum.USER)
        : (Object.values(RoleEnum).includes(rawData.role?.role as RoleEnum) ? rawData.role?.role as RoleEnum : RoleEnum.USER),
      companyName: rawData.companyName || '',
      workspaceName: rawData.workspaceName || '',
      confirmed: rawData.confirmed || false,
      integrations: rawData.integrations || [],
      password: '', // This is not returned by the API for security reasons
      primaryCompanyId: primaryId,
      companies: companiesArray,
    };

    // Validate required fields
    const requiredFields: RequiredUserFields[] = ['_id', 'email', 'firstName', 'lastName'];
    const missingFields = requiredFields.filter(field => !data[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    // Store user ID and company ID in localStorage for easy access by billing and other services
    localStorage.setItem('userId', data._id);
    
    // Keep a tab's active subaccount. /me must not bounce the operator back to primaryCompanyId.
    const activeCompanyId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('activeCompanyId') : null;
    const companyIdToStore = activeCompanyId || primaryId || (companiesArray && companiesArray.length > 0 ? companiesArray[0] : null);
    if (companyIdToStore && !activeCompanyId) {
      localStorage.setItem('companyId', companyIdToStore);
      
      // Store company name from user data
      if (rawData.companyName) {
        localStorage.setItem('companyName', rawData.companyName);
      } else if (rawData.workspaceName) {
        // Fallback to workspaceName if companyName not set
        localStorage.setItem('companyName', rawData.workspaceName);
      }
    } else if (activeCompanyId) {
      localStorage.setItem('companyId', activeCompanyId);
    }

    // Update cache
    userDataCache.data = data;
    userDataCache.timestamp = Date.now();

    return data;
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      redirectToSignIn();
      throw error;
    }
    throw error;
  }
}

export async function checkAuth(token: string | null) {
  try {
    console.log('Attempting auth check with token:', token ? `${token.substring(0, 10)}...` : 'null');
    
    if (!token) {
      console.warn('No token provided for auth check');
      throw new Error('No token provided');
    }

    // Use direct ky call to auth service instead of the api client
    // In production/staging, use AUTH_URL; in dev, use localhost
    const decodeUrl = isDevelopment 
      ? `http://localhost:${SERVICE_PORTS.AUTH}/api/auth/decode`
      : `${AUTH_URL}/api/auth/decode`;
    console.log('Making request to auth service:', decodeUrl);
    console.log('Request payload:', { token: token.substring(0, 10) + '...' });
    
    const response = await ky.post(decodeUrl, {
      headers: {
        "Content-Type": "application/json",
      },
      json: { token },
      timeout: 8000,
      retry: {
        limit: 2,
        methods: ['POST'],
        statusCodes: [408, 429, 500, 502, 503, 504],
      }
    }).json<{ user: UserType }>()

    console.log('Auth check successful, user data received:', response);
    return response.user
  } catch (error) {
    console.error('Auth check failed:', error);
    if (error instanceof Error) {
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      notification.error({ 
        placement: "topRight", 
        message: "Auth check failed", 
        description: error.message === 'Request timed out' ? 'Connection to server timed out. Please check your network.' : error.message
      });
    }
    throw error
  }
}

export async function confirmUser(token: string): Promise<ResponseType> {
  try {
    console.log('[API] Confirming user account with token:', token.substring(0, 15) + '...');
    return api.post(CONFIRM_USER, {
      headers: {
        "Authorization": token ? `Bearer ${token}` : undefined,
        "Content-Type": "application/json",
      },
      json: {
        token: token  // Also include the token in the request body
      }
    }).json()
  } catch (error) {
    console.error('[API] Error confirming user:', error);
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `User confirmation failed: ${error.message}` })
    throw error
  }
}

export async function checkEmailExists(email: string): Promise<boolean> {
  try {
    console.log('[checkEmailExists] Checking email:', email);
    // Use the public endpoint which doesn't require authentication
    const response = await ky.get(`${USERS_BASE_URL}/api/users/public/byEmail/${encodeURIComponent(email)}`, {
      timeout: 5000,
      retry: 0
    }).json<any>();
    
    console.log('[checkEmailExists] Response received:', { hasId: !!(response?._id || response?.id), statusCode: response?.statusCode });
    
    // Check if it's a 404 response (user not found)
    if (response?.statusCode === 404 || response?.message === 'User not found') {
      console.log('[checkEmailExists] Email available (not found)');
      return false;
    }
    
    // If we get a successful response with user data, the email exists
    const exists = !!(response?._id || response?.id);
    console.log('[checkEmailExists] Email exists:', exists);
    return exists;
  } catch (error) {
    // 404 means user not found, which is what we want (email available)
    if (error instanceof HTTPError && error.response.status === 404) {
      console.log('[checkEmailExists] Email available (404 error)');
      return false;
    }
    // For other errors, assume email doesn't exist to avoid blocking signup
    console.error('[checkEmailExists] Error checking email existence:', error);
    return false;
  }
}

function signInFailureFromBody(errorText: string, status?: number): SignInFailure {
  try {
    const errorData = JSON.parse(errorText);
    return describeSignInFailure({
      status,
      message: errorData.message || errorData.error,
      code: errorData.code,
      requiresPasswordChange: errorData.requiresPasswordChange,
      requiresMFAEnrollment: errorData.requiresMFAEnrollment,
    });
  } catch {
    return describeSignInFailure({ status, message: errorText });
  }
}

export async function signIn(email: string, password: string): Promise<ResponseType> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    // In development, requests will be proxied through Vite
    // For production/staging, use AUTH_URL if set, otherwise use window.location.origin
    // This ensures staging uses the correct API URL
    const isDev = import.meta.env.DEV || process.env.NODE_ENV === 'development';
    // Use AUTH_URL if available, otherwise fall back to window.location.origin for same-origin requests
    const baseUrl = isDev ? '' : (AUTH_URL || window.location.origin);
    
    console.log('[SignIn] Starting API call with:', {
      url: `${baseUrl}/api/auth/sign-in`,
      email,
      isDev,
      baseUrl,
      origin: window.location.origin
    });
    
    // Get client's real IP address for login audit
    const realIp = await fetchClientIp();
    
    const requestOptions = {
      headers: signInClientHeaders(realIp),
      json: { email, password },
      timeout: 10000,
      signal: controller.signal,
      retry: { limit: 0 },
      credentials: 'include' as const,
      mode: 'cors' as const
    };

    try {
      console.log('[SignIn] Attempting primary request with ky');
      const response = await ky.post(`${baseUrl}/api/auth/sign-in`, requestOptions).json<ResponseType>();
      
      console.log('[SignIn] Primary request successful:', {
        mfaRequired: 'mfaRequired' in response ? response.mfaRequired : false,
        hasAccessToken: 'accessToken' in response ? !!response.accessToken : false,
        hasRefreshToken: 'refreshToken' in response ? !!response.refreshToken : false,
        hasUser: 'user' in response ? !!response.user : false
      });
      
      clearTimeout(timeoutId);
      
      // Only store tokens if this is a successful login (no MFA required)
      if ('accessToken' in response && response.accessToken) {
        localStorage.setItem('accessToken', response.accessToken);
        console.log('[SignIn] Access token stored');
      }
      if ('refreshToken' in response && response.refreshToken) {
        localStorage.setItem('refreshToken', response.refreshToken);
        console.log('[SignIn] Refresh token stored');
      }

      return response;
    } catch (kyError) {
      console.error('[SignIn] Primary request failed:', {
        error: kyError,
        status: kyError instanceof HTTPError ? kyError.response.status : 'N/A',
        statusText: kyError instanceof HTTPError ? kyError.response.statusText : 'N/A',
        url: kyError instanceof HTTPError ? kyError.request.url : 'N/A'
      });
      
      if (kyError instanceof HTTPError) {
        const errorText = await kyError.response.text();
        console.error('[SignIn] Error response body:', errorText);
        (kyError as HTTPError & { signInFailure?: SignInFailure }).signInFailure = signInFailureFromBody(
          errorText,
          kyError.response.status,
        );
      }
      
      throw kyError;
    }
  } catch (error) {
    console.error('[SignIn] Error details:', {
      name: error instanceof Error ? error.name : 'Unknown Error',
      message: error instanceof Error ? error.message : String(error),
      status: (error as any)?.response?.status,
      statusText: (error as any)?.response?.statusText,
      headers: (error as any)?.response?.headers
    });

    if (error instanceof Error) {
      const failure = (error as Error & { signInFailure?: SignInFailure }).signInFailure
        || (error instanceof DOMException && error.name === 'AbortError'
          ? describeSignInFailure({ message: 'timed out', status: 503 })
          : describeSignInFailure({
              status: error instanceof HTTPError ? error.response.status : (error as { response?: { status?: number } }).response?.status,
              message: error.message,
            }));
      (error as Error & { signInFailure?: SignInFailure }).signInFailure = failure;
      notification.error({
        key: 'sign-in-error',
        placement: "topRight",
        message: failure.title,
        description: failure.description,
        duration: 8
      });
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function verifyMFA(userId: string, token: string, method: string): Promise<ResponseType> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const isDev = process.env.NODE_ENV === 'development';
    const baseUrl = isDev ? '' : AUTH_URL;
    
    console.log('[VerifyMFA] Starting MFA verification with:', {
      url: `${baseUrl}/api/auth/verify-mfa`,
      userId,
      method,
      hasToken: !!token
    });
    
    const realIp = await fetchClientIp();
    const requestOptions = {
      headers: signInClientHeaders(realIp),
      json: { userId, token, method },
      timeout: 10000,
      signal: controller.signal,
      retry: {
        limit: 2,
        methods: ['POST'],
        statusCodes: [408, 429, 500, 502, 503, 504],
        backoffLimit: 3000
      },
      credentials: 'include' as const,
      mode: 'cors' as const
    };

    try {
      console.log('[VerifyMFA] Attempting MFA verification request');
      const response = await ky.post(`${baseUrl}/api/auth/verify-mfa`, requestOptions).json<ResponseType>();
      
      console.log('[VerifyMFA] MFA verification successful:', {
        hasAccessToken: 'accessToken' in response ? !!response.accessToken : false,
        hasRefreshToken: 'refreshToken' in response ? !!response.refreshToken : false,
        hasUser: 'user' in response ? !!response.user : false
      });
      
      clearTimeout(timeoutId);
      
      // Store tokens if verification was successful
      if ('accessToken' in response && response.accessToken) {
        localStorage.setItem('accessToken', response.accessToken);
        console.log('[VerifyMFA] Access token stored');
      }
      if ('refreshToken' in response && response.refreshToken) {
        localStorage.setItem('refreshToken', response.refreshToken);
        console.log('[VerifyMFA] Refresh token stored');
      }

      return response;
    } catch (kyError) {
      console.error('[VerifyMFA] MFA verification failed:', {
        error: kyError,
        status: kyError instanceof HTTPError ? kyError.response.status : 'N/A',
        statusText: kyError instanceof HTTPError ? kyError.response.statusText : 'N/A',
        url: kyError instanceof HTTPError ? kyError.request.url : 'N/A'
      });
      
      if (kyError instanceof HTTPError) {
        const errorText = await kyError.response.text();
        console.error('[VerifyMFA] Error response body:', errorText);
      }
      
      throw kyError;
    }
  } catch (error) {
    console.error('[VerifyMFA] Error details:', {
      name: error instanceof Error ? error.name : 'Unknown Error',
      message: error instanceof Error ? error.message : String(error),
      status: (error as any)?.response?.status,
      statusText: (error as any)?.response?.statusText
    });

    if (error instanceof Error) {
      let errorMessage = error.message;
      if (error instanceof DOMException && error.name === 'AbortError') {
        errorMessage = 'MFA verification request timed out. Please try again.';
      } else if (error instanceof HTTPError && error.response.status === 401) {
        errorMessage = 'Invalid MFA token';
      } else if (error instanceof HTTPError && error.response.status === 400) {
        errorMessage = 'Invalid MFA request';
      }
      
      notification.error({ 
        placement: "topRight", 
        message: "MFA verification failed", 
        description: errorMessage,
        duration: 5
      });
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function notifySignupStarted(lead: {
  email: string
  firstName?: string
  lastName?: string
  companyName?: string
}): void {
  const email = lead.email?.trim()
  if (!email) return
  fetch(`${BASE_URL}/api/signup/started`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      firstName: lead.firstName || "",
      lastName: lead.lastName || "",
      companyName: lead.companyName || "",
    }),
    keepalive: true,
  }).catch(() => undefined)
}

export async function registration(
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  companyName?: string,
  productTier?: 'free-trial' | 'starter' | 'advanced' | 'enterprise',
  paymentMethodId?: string
) {
  try {
    return ky(`${BASE_URL}/${REGISTRATION_PATH}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ 
        email, 
        password, 
        firstName, 
        lastName, 
        companyName, 
        productTier,
        paymentMethodId 
      }),
      //hooks: { afterResponse: [errorNotification] },
    }).json()
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Registration failed: ${error.message}` })
    throw error
  }
}

// Payment Methods API
export async function getPaymentMethods(): Promise<any> {
  try {
    const token = localStorage.getItem('accessToken');
    const tokenValue = token && token.trim().startsWith('Bearer ') ? token.trim() : `Bearer ${token?.trim()}`;
    
    return await ky.get(`${BASE_URL}/${SERVICE_PATHS.API.substring(1)}/payment-methods`, {
      headers: {
        Authorization: tokenValue,
        'Content-Type': 'application/json',
      }
    }).json();
  } catch (error) {
    console.error('Error fetching payment methods:', error);
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Failed to fetch payment methods: ${error.message}` });
    throw error;
  }
}

export async function handleRefreshToken(
  refreshToken: string,
  options?: { silent?: boolean }
): Promise<RefreshTokenResponse> {
  try {
    // In production/staging, use API base URL so refresh always hits the API (avoids website-host routing)
    const refreshUrl = isDevelopment 
      ? `${AUTH_URL}/api/auth/refresh`
      : `${BASE_URL || window.location.origin}/api/auth/refresh`;
    
    const response = await ky.post(refreshUrl, {
      headers: {
        "Content-Type": "application/json",
      },
      json: { refreshToken },
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data = await response.json<RefreshTokenResponse>();
    
    if (!data.accessToken || !data.refreshToken) {
      throw new Error('Invalid refresh response');
    }

    return data;
  } catch (error) {
    console.error('[Frontend API] Token refresh failed:', error);
    if (!options?.silent && error instanceof Error) {
      notification.error({ 
        placement: "topRight", 
        message: "Session refresh failed", 
        description: "Please log in again to continue."
      });
    }
    throw error;
  }
}

export async function resetPassword(email: string) {
  try {
    return ky(`${BASE_URL}/${RESET_PASSWORD_PATH}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
      //hooks: { afterResponse: [errorNotification] },
    }).json()
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Password reset failed: ${error.message}` })
    throw error
  }
}

export async function updatePassword(password: string, token: string) {
  try {
    return ky(`${BASE_URL}/${UPDATE_PASSWORD_PATH}`, {
      method: "POST",
      headers: {
        "Authorization": token ? `Bearer ${token}` : undefined,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ password: { new: password } }),
      //hooks: { afterResponse: [errorNotification] },
    }).json()
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Password update failed: ${error.message}` })
    throw error
  }
}

export async function getAllConnectors(token: string): Promise<DataConnectorsType[]> {
  try {
    // Use cached user data if available
    const userData = userDataCache.data || await getMe();
    const userId = userData._id;

    if (!userId) {
      throw new Error('No user ID found in user data');
    }

    // Use the company-wide endpoint to get all connectors in the company
    const connectors = await api
      .get(`${ALL_CONNECTORS_PATH}/by-company-query`, {
        searchParams: {
          userId
        }
      })
      .json<any[]>();
    
    // Transform the raw connector data to the expected DataConnectorsType format
    return connectors.map(connector => ({
      connector: {
        _id: connector._id,
        name: connector.name,
        connectorType: connector.connectorType,
        details: connector.details || "",
        status: connector.status,
        integrationName: connector.integrationName || "",
        budgetLimit: connector.budgetLimit,
        computeSize: connector.computeSize || "",
        configId: connector.configId,
        createdAt: connector.createdAt,
        updatedAt: connector.updatedAt,
        encryptionOption: connector.encryptionOption || "",
        supportPackage: connector.supportPackage || "",
        storeLocally: connector.storeLocally
      },
      config: connector.config || {
        _id: connector.configId,
        integrationId: connector._id,
        name: connector.name,
        status: connector.status
      }
    }));
  } catch (error) {
    console.error('[Frontend API] getAllConnectors error:', error);
    if (error instanceof Error) {
      notification.error({ 
        placement: "topRight", 
        message: "Failed to fetch connectors", 
        description: error.message 
      });
    }
    throw error;
  }
}

export async function getCompanyConnectors(token: string): Promise<DataConnectorsType[]> {
  try {
    // Use cached user data if available
    const userData = userDataCache.data || await getMe();
    const userId = userData._id;

    if (!userId) {
      throw new Error('No user ID found in user data');
    }

    // Use the company-wide endpoint to get all connectors in the company
    const connectors = await api
      .get(`${ALL_CONNECTORS_PATH}/by-company-query`, {
        searchParams: {
          userId
        }
      })
      .json<any[]>();
    
    // Transform the raw connector data to the expected DataConnectorsType format
    return connectors.map(connector => ({
      connector: {
        _id: connector._id,
        name: connector.name,
        connectorType: connector.connectorType,
        details: connector.details || "",
        status: connector.status,
        integrationName: connector.integrationName || "",
        budgetLimit: connector.budgetLimit,
        computeSize: connector.computeSize || "",
        configId: connector.configId,
        createdAt: connector.createdAt,
        updatedAt: connector.updatedAt,
        encryptionOption: connector.encryptionOption || "",
        supportPackage: connector.supportPackage || "",
        storeLocally: connector.storeLocally
      },
      config: connector.config || {
        _id: connector.configId,
        integrationId: connector._id,
        name: connector.name,
        status: connector.status
      }
    }));
  } catch (error) {
    console.error('[Frontend API] getCompanyConnectors error:', error);
    if (error instanceof Error) {
      notification.error({ 
        placement: "topRight", 
        message: "Failed to fetch company connectors", 
        description: error.message 
      });
    }
    throw error;
  }
}

export async function getDashboardConnectors(token: string, silent: boolean = false): Promise<DataConnectorsType[]> {
  try {
    const sessionUser = useAuthStore.getState().user;
    if (sessionUser?._id && userDataCache.data && String(userDataCache.data._id) !== String(sessionUser._id)) {
      clearUserDataCache();
    }
    const userData = sessionUser?._id ? sessionUser : (userDataCache.data || await getMe());
    const userId = userData._id;

    if (!userId) {
      throw new Error('No user ID found in user data');
    }

    // Ensure token is properly formatted
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

    // Use the dashboard-specific endpoint that doesn't fetch configs
    // Explicitly pass the token in headers to ensure it's used
    const connectors = await api
      .get(`${ALL_CONNECTORS_PATH}/dashboard-user-query`, {
        searchParams: {
          userId
        },
        headers: {
          'Authorization': authToken
        }
      })
      .json<any[]>();
    
    // Transform the raw connector data to the expected DataConnectorsType format
    return connectors.map(connector => ({
      connector: {
        _id: connector._id,
        name: connector.name,
        connectorType: connector.connectorType,
        details: "", // Not needed for dashboard
        status: connector.status,
        lastSyncAt: connector.lastSyncAt,
        supportPackage: connector.supportPackage || "",
        masterSync: connector.masterSync || false,
        // Use integrationProfile.integrationName first, then integrationName, then fall back to name
        integrationName: connector.integrationProfile?.integrationName || connector.integrationName || connector.name || "Unnamed Integration",
        integrationMode: connector.integrationMode || "pipeline",
        syncCadence: connector.syncCadence,
        budgetLimit: 0, // Not needed for dashboard
        computeSize: "", // Not needed for dashboard
        configId: "", // Not needed for dashboard
        createdAt: connector.createdAt,
        updatedAt: connector.updatedAt,
        encryptionOption: "", // Not needed for dashboard
        storeLocally: false // Not needed for dashboard
      },
      // 🔥 FIX: Use the FULL config from backend (includes all emailConfig, filters, etc)
      // The backend provides config.source and config.destination with complete data
      config: connector.config ? {
        // Use full source/destination objects from backend, not just minimal fields
        source: connector.config.source,
        destination: connector.config.destination,
        name: connector.integrationProfile?.integrationName || connector.name
      } : {
        // Fallback to minimal config only if backend didn't send config
        source: connector.sourceConfiguration ? {
          url: connector.sourceConfiguration.url,
          requestMethod: connector.sourceConfiguration.requestMethod,
          type: connector.sourceConfiguration.type || 'REST API'
        } : undefined,
        destination: connector.destinationConfiguration ? {
          url: connector.destinationConfiguration.url,
          requestMethod: connector.destinationConfiguration.requestMethod,
          type: connector.destinationConfiguration.type || 'REST API'
        } : undefined,
        name: connector.integrationProfile?.integrationName || connector.name
      }
    }));
  } catch (error) {
    console.error('[Frontend API] getDashboardConnectors error:', error);
    if (!silent && error instanceof Error) {
      notification.error({
        key: 'dashboard-connectors',
        placement: "topRight",
        message: "Failed to fetch dashboard connectors",
        description: error.message
      });
    }
    throw error;
  }
}

export async function getConnector(id: string, token: string, silent: boolean = false): Promise<DataConnectorsType> {
  try {
    return api
      .get(`${ALL_CONNECTORS_PATH}/${id}`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      })
      .json()
  } catch (error) {
    // Only show error notification if not in silent mode and the error is not due to missing ID
    if (!silent && error instanceof Error && id && id !== 'undefined') {
      notification.error({ placement: "topRight", message: `Get connector by id failed: ${error.message}` })
    }
    throw error
  }
}

export async function getEstimatedMonthlyTokenUsage(id: string, token: string): Promise<number> {
  try {
    const response = await api
      .get(`${ALL_CONNECTORS_PATH}/${id}/estimated-monthly-tokens`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      })
      .json<{ estimatedMonthlyTokens: number }>()
    return response?.estimatedMonthlyTokens || 0
  } catch (error) {
    console.warn(`Failed to fetch estimated monthly token usage for integration ${id}:`, error)
    return 0 // Return 0 on error to prevent UI breakage
  }
}

export async function createConnector(
  body: any,
  token: string
): Promise<DataConnectorsType> {
  try {
    console.log('[API] Creating Integration with token:', token ? `${token.substring(0, 15)}...` : 'no token');
    console.log('[API] Integration creation payload:', JSON.stringify(body, null, 2).substring(0, 500) + '...');

    // Ensure token is properly formatted
    const formattedToken = token && token.trim().startsWith('Bearer ') 
      ? token.trim() 
      : `Bearer ${token?.trim()}`;
    
    // Set a longer timeout and more aggressive retry strategy
    const response = await api.post(CREATE_CONNECTOR_PATH, {
      headers: {
        "Authorization": formattedToken,
        "Content-Type": "application/json",
      },
      json: body,
              timeout: 60000, // 60 seconds timeout
      retry: {
        limit: 3,
        methods: ['POST'],
        statusCodes: [401, 408, 413, 429, 500, 502, 503, 504],
        maxRetryAfter: 10,
      }
    });

    // Log successful response
    console.log('[API] Integration created successfully, status:', response.status);
    return response.json();
  } catch (error) {
    console.error('[API] Create Integration error:', error);
    
    // Detailed error logging
    if (error instanceof HTTPError) {
      console.error('[API] HTTP Error details:', {
        status: error.response.status,
        statusText: error.response.statusText,
        url: error.request.url,
        headers: Object.fromEntries(error.request.headers)
      });
      
      try {
        // Try to parse the error response body
        const errorBody = await error.response.text();
        console.error('[API] Error response body:', errorBody);
      } catch (parseError) {
        console.error('[API] Could not parse error response body');
      }
    }
    
    // User friendly error notification
    if (error instanceof Error) {
      let errorMessage = `Create Integration failed: ${error.message}`;
      let description = '';
      
      // Handle specific error types
      if (error.message.includes('Multipart: Unexpected end of form')) {
        errorMessage = 'Create Integration failed';
        description = 'File upload was interrupted. Please try again with a smaller file or a more stable connection.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Create Integration failed';
        description = 'Request timed out. The server took too long to respond. Please try again.';
      } else if (error.message.includes('NetworkError')) {
        errorMessage = 'Create Integration failed';
        description = 'Network error occurred. Please check your internet connection and try again.';
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        errorMessage = 'Create Integration failed';
        description = 'Authentication error. Please sign out and sign in again.';
        
        // Attempt token refresh
        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            const newTokens = await handleRefreshToken(refreshToken);
            if (newTokens.accessToken) {
              localStorage.setItem('accessToken', newTokens.accessToken);
              localStorage.setItem('refreshToken', newTokens.refreshToken);
              
              // Show a notification about retrying
              notification.info({
                placement: "topRight",
                message: "Session refreshed",
                description: "Please try publishing your integration again.",
                duration: 5
              });
              
              return Promise.reject(new Error('Authentication refreshed, please try again'));
            }
          }
        } catch (refreshError) {
          console.error('[API] Token refresh failed:', refreshError);
          description = 'Authentication failed. Please sign in again.';
          
          // Clear tokens and redirect to login after a brief delay
          setTimeout(() => {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            redirectToSignIn();
          }, 3000);
        }
      }
      
      notification.error({ 
        placement: "topRight", 
        message: errorMessage, 
        description: description || errorMessage,
        duration: 8
      });
    }
    throw error;
  }
}

export async function updateConnector(body: any, token: string): Promise<DataConnectorsType> {
  try {
    return api
      .patch(`${UPDATE_CONNECTOR_PATH}connectors/${body.id}`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
        json: body,
        timeout: 60000, // 60 second timeout for updates
        retry: {
          limit: 3,
          methods: ['PATCH'],
          statusCodes: [408, 429, 500, 502, 503, 504],
          backoffLimit: 5000
        }
      })
      .json();
  } catch (error) {
    if (error instanceof Error) {
      let errorMessage = `Update connector failed: ${error.message}`;
      if (error.message.includes('timeout')) {
        errorMessage = 'Update request timed out. Please try again or check your network connection.';
      }
      notification.error({ 
        placement: "topRight", 
        message: "Update connector failed", 
        description: errorMessage,
        duration: 8
      });
    }
    throw error
  }
}

export async function deployConnector(connectorId: string, token: string): Promise<any> {
  try {
    return api
      .post(`${UPDATE_CONNECTOR_PATH}connectors/${connectorId}/deploy`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
        timeout: 60000, // 60 second timeout for deployment
        retry: {
          limit: 2,
          methods: ['POST'],
          statusCodes: [408, 429, 500, 502, 503, 504],
          backoffLimit: 10000
        }
      })
      .json();
  } catch (error) {
    if (error instanceof Error) {
      let errorMessage = `Deploy connector failed: ${error.message}`;
      if (error.message.includes('timeout')) {
        errorMessage = 'Deployment request timed out. The deployment may still be in progress. Please check the deployment status.';
      }
      notification.error({ 
        placement: "topRight", 
        message: "Deploy connector failed", 
        description: errorMessage,
        duration: 8
      });
    }
    throw error
  }
}

export async function getDeploymentStatus(connectorId: string, token: string): Promise<any> {
  try {
    return api
      .get(`${UPDATE_CONNECTOR_PATH}connectors/${connectorId}/deployment-status`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
        timeout: 10000, // 10 second timeout for status checks
        retry: {
          limit: 3,
          methods: ['GET'],
          statusCodes: [408, 429, 500, 502, 503, 504],
          backoffLimit: 5000
        }
      })
      .json();
  } catch (error) {
    console.error('Error fetching deployment status:', error);
    throw error
  }
}

export async function deleteConnector(id: string, token: string): Promise<void> {
  try {
    await api
      .delete(`${DELETE_CONNECTOR}${id}`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
        },
      });
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Delete connector failed: ${error.message}` })
    throw error
  }
}

async function readApiError(error: unknown): Promise<string> {
  const response = (error as { response?: Response })?.response
  if (response) {
    try {
      const text = await response.clone().text()
      const data = JSON.parse(text)
      if (typeof data?.message === "string") return data.message
      if (Array.isArray(data?.message)) return data.message.join(", ")
      if (typeof data?.error_description === "string") return data.error_description
    } catch {
      // fall through
    }
  }
  return error instanceof Error ? error.message : "Request failed"
}

export async function executeOAuth2PasswordGrant(body: any): Promise<any> {
  try {
    return await api.post("api/advanced-auth/oauth2/execute", { json: body }).json()
  } catch (error) {
    throw new Error(await readApiError(error))
  }
}

export async function ensureOAuth2PasswordGrant(body: any): Promise<any> {
  try {
    return await api.post("api/advanced-auth/oauth2/ensure", { json: body }).json()
  } catch (error) {
    throw new Error(await readApiError(error))
  }
}

export async function executeOAuth2ClientCredentials(oauth2: {
  tokenUrl?: string
  clientId?: string
  clientSecret?: string
  audience?: string
  scope?: string
}): Promise<any> {
  try {
    return await api.post("api/advanced-auth/oauth2/execute", {
      json: {
        authFlow: "oauth2ClientCredentials",
        oauth2: {
          ...oauth2,
          grantType: "client_credentials",
        },
      },
    }).json()
  } catch (error) {
    throw new Error(await readApiError(error))
  }
}

export async function validateConfig(body: any, token: string): Promise<any> {
  try {
    const response = await api
      .post(VALIDATE_CONFIG, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
        json: body,
      });
    return response.json();
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Validate config failed: ${error.message}` })
    throw error
  }
}

export async function validateDataMapping(body: any, token: string): Promise<any> {
  try {
    // Handle both formats: array of edges or object with dataMapping property
    const payload = Array.isArray(body) 
      ? { dataMapping: body } 
      : body;
      
    const response = await api
      .post(VALIDATE_DATA_MAPPING, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
        json: payload,
        timeout: 15000, // 15 second timeout
        retry: {
          limit: 3,
          methods: ['POST'],
          statusCodes: [408, 429, 500, 502, 503, 504],
          backoffLimit: 10000
        }
      });
    return response.json();
  } catch (error) {
    if (error instanceof Error) {
      let errorMessage = `Validate data mapping failed: ${error.message}`;
      if (error.message.includes('timeout')) {
        errorMessage = 'Request timed out. The server might be busy or the data mapping is complex. Please try again.';
      }
      notification.error({ 
        placement: "topRight", 
        message: "Validation Failed", 
        description: errorMessage,
        duration: 5
      });
    }
    throw error;
  }
}

export async function validateDataMappingRule(body: any, token: string): Promise<any> {
  try {
    return api
      .post(`${UPDATE_CONNECTOR_PATH}connectors/validateMappingRule`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
        json: body,
      })
      .json();
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Validate mapping rule failed: ${error.message}` })
    throw error
  }
}

export async function saveMappingRule(body: any, token: string): Promise<any> {
  try {
    return api
      .post(`${UPDATE_CONNECTOR_PATH}connectors/saveMappingRule`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
        json: body,
      })
      .json();
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Save mapping rule failed: ${error.message}` })
    throw error
  }
}

export async function getMappingRules(token: string): Promise<any> {
  try {
    return api
      .get(`${UPDATE_CONNECTOR_PATH}connectors/mappingRules`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      })
      .json();
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Get mapping rules failed: ${error.message}` })
    throw error
  }
}

export async function deleteMappingRule(ruleId: string, token: string): Promise<any> {
  try {
    return api
      .delete(`${UPDATE_CONNECTOR_PATH}connectors/mappingRules/${ruleId}`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      })
      .json();
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Delete mapping rule failed: ${error.message}` })
    throw error
  }
}

export async function pauseConnector(id: string, token: string): Promise<void> {
  try {
    const result = await api
      .post(`${SERVICE_PATHS.CONNECTORS.substring(1)}/pause/${id}`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
        },
      })
      .json<any>();
    if (result?.warning) {
      notification.warning({
        placement: "topRight",
        message: "Paused in Datacord, pods not stopped",
        description: result.warning,
        duration: 8,
      });
    }
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Pause connector failed: ${error.message}` })
    throw error
  }
}

export async function resumeConnector(id: string, token: string): Promise<void> {
  try {
    const result = await api
      .post(`${RESUME_CONNECTOR}/${id}`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
        },
        timeout: 60000, // 60 second timeout
        retry: {
          limit: 3,
          methods: ['POST'],
          statusCodes: [408, 429, 500, 502, 503, 504],
          backoffLimit: 5000
        }
      })
      .json<any>();
    if (result?.warning) {
      notification.warning({
        placement: "topRight",
        message: "Resumed in Datacord, pods not restarted",
        description: result.warning,
        duration: 8,
      });
    }
  } catch (error) {
    if (error instanceof Error) {
      let errorMessage = `Resume connector failed: ${error.message}`;
      if (error.message.includes('timeout')) {
        errorMessage = 'Request timed out. The connector is taking longer than expected to resume. Please try again.';
      }
      notification.error({ 
        placement: "topRight", 
        message: "Resume connector failed", 
        description: errorMessage,
        duration: 5
      });
    }
    throw error;
  }
}

/**
 * @deprecated getAllConfigs() is deprecated as of October 2025.
 * Configuration data has been migrated to connectors collection.
 * Use getConnectors() instead - configs are now in connector.sourceConfiguration, connector.destinationConfiguration
 * 
 * This function will be removed in a future release.
 */
export async function getAllConfigs(token: string): Promise<ConfigType[]> {
  console.warn('⚠️  DEPRECATED: getAllConfigs() is deprecated. Use getConnectors() instead.');
  console.warn('   Config data is now embedded in connectors: connector.sourceConfiguration, connector.destinationConfiguration');
  
  try {
    const configs = await api
      .get(ALL_CONFIGS_PATH, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
        },
      })
      .json<ConfigType[]>();
    return configs;
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Get all configs failed: ${error.message}` })
    throw error
  }
}

export async function getDashboardAnalytics(
  integrationIds: string[],
  startDate: string,
  endDate: string,
  interval: string = 'day',
  token: string
): Promise<AnalyticsResponseType> {
  try {
    const analytics = await api
      .post(GET_DASHBOARD_ANALYTICS, {
        json: {
          integrationIds,
          startDate,
          endDate,
          interval
        },
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
        },
      })
      .json<AnalyticsResponseType>();
    return analytics;
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Get dashboard analytics failed: ${error.message}` })
    throw error
  }
}

export async function getStatistic(token: string, filter: { integrationId: string; startDate: string; endDate: string }): Promise<StatisticType> {
  try {
    const response = await api
      .get('api/analytics/statistic', {
        searchParams: {
          integrationId: filter.integrationId,
          startDate: filter.startDate,
          endDate: filter.endDate
        },
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
        },
      })
      .json<StatisticType>();
    return response;
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Get statistics failed: ${error.message}` })
    throw error
  }
}

export async function getTransactions(
  token: string, 
  integrationId: string,
  page: number = 1,
  pageSize: number = 250,
  startDate?: string,
  endDate?: string
): Promise<{ 
  transactions: TransactionsAnalyticsData[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }
}> {
  try {
    const searchParams: Record<string, string | number | boolean> = {
      page,
      pageSize,
      loadAll: true
    };
    
    // Add date filters if provided
    if (startDate) searchParams.startDate = startDate;
    if (endDate) searchParams.endDate = endDate;
    
    // Website origin has no /api proxy. Always use the API host (same as dashboard analytics).
    const baseUrl = BASE_URL || (isDevelopment
      ? `http://localhost:${SERVICE_PORTS.API}`
      : 'https://api.datacord.com');
      
    const url = new URL(`/api/analytics/transactions/${integrationId}`, baseUrl);
    
    // Add all search params to the URL
    Object.entries(searchParams).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    return data;
  } catch (error) {
    console.error('API Error fetching transactions:', error);
    // Bubble up the error to be handled by the caller
    throw error;
  }
}

export async function getTransactionById(
  token: string,
  integrationId: string,
  transactionId: string
): Promise<TransactionsAnalyticsData> {
  try {
    const baseUrl = BASE_URL || (isDevelopment
      ? `http://localhost:${SERVICE_PORTS.API}`
      : 'https://api.datacord.com');
      
    const url = new URL(`/api/analytics/transactions/${integrationId}/${transactionId}`, baseUrl);
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('API Error fetching transaction details:', error);
    throw error;
  }
}

export async function getEndpointScores(
  token: string,
  integrationId: string,
  sourceStartpoint?: string,
  destinationEndpoint?: string
): Promise<{
  sourceStartpoint?: {
    endpoint: string;
    score: number;
    errorRate: number;
    avgSpeed: number;
    avgPayloadSize: number;
    totalTransactions: number;
    successCount: number;
    errorCount: number;
    performanceGrade: 'excellent' | 'good' | 'fair' | 'poor';
  };
  destinationEndpoint?: {
    endpoint: string;
    score: number;
    errorRate: number;
    avgSpeed: number;
    avgPayloadSize: number;
    totalTransactions: number;
    successCount: number;
    errorCount: number;
    performanceGrade: 'excellent' | 'good' | 'fair' | 'poor';
  };
}> {
  try {
    // Call connectors service directly (not through API gateway)
    // In production/staging, use window.location.origin; in dev, use localhost
    const baseUrl = isDevelopment 
      ? `http://localhost:${SERVICE_PORTS.CONNECTORS}` 
      : window.location.origin;
      
    const url = new URL(`/api-connectors/endpoint-scores/${integrationId}`, baseUrl);
    
    // Only add valid URLs (must be complete HTTP/HTTPS URLs)
    if (sourceStartpoint && sourceStartpoint.startsWith('http')) {
      url.searchParams.append('sourceStartpoint', sourceStartpoint);
    }
    if (destinationEndpoint && destinationEndpoint.startsWith('http')) {
      url.searchParams.append('destinationEndpoint', destinationEndpoint);
    }
    
    console.log('[API] Fetching endpoint scores:', {
      url: url.toString(),
      hasSource: !!sourceStartpoint,
      hasDest: !!destinationEndpoint
    });
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      // If endpoint doesn't exist (404), return empty scores instead of throwing
      if (response.status === 404) {
        console.warn('[API] Endpoint scores not available (404) - returning defaults');
        return {};
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('[API] Endpoint scores response:', data);
    return data;
  } catch (error) {
    // Gracefully handle errors - Dashboard should still load
    console.error('API Error fetching endpoint scores:', error);
    return {}; // Return empty object instead of throwing
  }
}

export async function getIntegrationHealthStatus(
  token: string,
  integrationId: string
): Promise<any> {
  try {
    // Call connectors service directly (not through API gateway)
    // In production/staging, use window.location.origin; in dev, use localhost
    const baseUrl = isDevelopment 
      ? `http://localhost:${SERVICE_PORTS.CONNECTORS}` 
      : window.location.origin;
      
    const url = new URL(`/api-connectors/${integrationId}/health-status`, baseUrl);
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      // If endpoint doesn't exist (404), return default health status
      if (response.status === 404) {
        console.warn('[API] Health status endpoint not available (404) - returning defaults');
        return { status: 'unknown', message: 'Health check not available' };
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.data || data;
  } catch (error) {
    // Gracefully handle errors - Dashboard should still load
    console.error('API Error fetching health status:', error);
    return { status: 'unknown', message: 'Health check unavailable' }; // Return default instead of throwing
  }
}

export async function getAllPublicConnectors(token: string): Promise<TemplatesType[]> {
  try {
    const connectors = await api
      .get(GET_ALL_PUBLIC_CONNECTORS, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
        },
        timeout: 60000, // Increase timeout to 60 seconds
        retry: {
          limit: 3,
          methods: ['GET'],
          statusCodes: [408, 429, 500, 502, 503, 504],
          backoffLimit: 3000
        }
      })
      .json<TemplatesType[]>();
    return connectors;
  } catch (error) {
    if (error instanceof Error) {
      notification.error({ 
        placement: "topRight", 
        message: "Failed to fetch templates",
        description: error.message
      });
    }
    throw error;
  }
}

export async function createPublicConnector(body: any, token: string): Promise<TemplatesType> {
  try {
    return api
      .post(CREATE_PUBLIC_CONNECTOR, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
        json: body,
      })
      .json();
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Create public connector failed: ${error.message}` })
    throw error
  }
}

export async function updatePublicConnector(body: any, token: string): Promise<TemplatesType> {
  try {
    return api
      .put(UPDATE_PUBLIC_CONNECTOR, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
        json: body,
      })
      .json();
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Update public connector failed: ${error.message}` })
    throw error
  }
}

export async function deletePublicConnector(id: string, token: string): Promise<void> {
  try {
    await api
      .delete(`${DELETE_PUBLIC_CONNECTOR}${id}`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
        },
      });
  } catch (error) {
    if (error instanceof Error)
      notification.error({ placement: "topRight", message: `Delete public connector failed: ${error.message}` })
    throw error
  }
}

export async function getTemplate(id: string, token: string): Promise<TemplatesType> {
  try {
    const template = await api
      .get(`${GET_ALL_PUBLIC_CONNECTORS}/${id}`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      })
      .json<TemplatesType>();
    return template;
  } catch (error) {
    if (error instanceof Error) {
      notification.error({ 
        placement: "topRight", 
        message: "Failed to fetch template",
        description: error.message
      });
    }
    throw error;
  }
}

// ==================== NOTIFICATIONS ====================

export interface NotificationHistoryItem {
  id?: string;
  userId: string;
  type: string;
  subject: string;
  content: string;
  category: string;
  sentAt: string | Date;
  status: 'delivered' | 'failed' | 'pending';
  messageId?: string;
  read?: boolean;
}

export interface PaginatedNotificationResponse {
  data: NotificationHistoryItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  unreadCount?: number;
}

export async function getNotifications(
  userId: string,
  page: number = 1,
  limit: number = 20
): Promise<PaginatedNotificationResponse> {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await api
      .get(`api/notifications/preferences/${userId}/history`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
        searchParams: {
          page: page.toString(),
          limit: limit.toString(),
          sortBy: 'sentAt',
          sortOrder: 'desc'
        }
      })
      .json<PaginatedNotificationResponse>();
    
    return response;
  } catch (error) {
    console.error('Failed to fetch notifications:', error);
    // Return empty data instead of showing error notification
    return {
      data: [],
      total: 0,
      page: 1,
      limit: 20,
      totalPages: 0
    };
  }
}

export async function getPushPublicKey(): Promise<string> {
  const token = localStorage.getItem('accessToken');
  const response = await api
    .get('api/notifications/push/public-key', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    })
    .json<{ publicKey: string }>();
  return response.publicKey;
}

export async function registerPushSubscription(userId: string, subscription: PushSubscriptionJSON): Promise<void> {
  const token = localStorage.getItem('accessToken');
  await api.post('api/notifications/push/subscribe', {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
      'Content-Type': 'application/json',
    },
    json: { userId, subscription },
  });
}

export async function getUnreadNotificationCount(userId: string): Promise<number> {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await api
      .get(`api/notifications/${userId}/unread-count`, {
        headers: {
          "Authorization": token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      })
      .json<{ unreadCount: number }>();

    return response.unreadCount ?? 0;
  } catch (error) {
    console.error('Failed to fetch unread notification count:', error);
    return 0;
  }
}

export async function markNotificationAsRead(notificationId: string, userId: string): Promise<void> {
  try {
    const token = localStorage.getItem('accessToken');
    await api.patch(`api/notifications/${notificationId}/read`, {
      headers: {
        "Authorization": token ? `Bearer ${token}` : undefined,
        "Content-Type": "application/json",
      },
      json: { userId },
    });
  } catch (error) {
    console.error('Failed to mark notification as read:', error);
  }
}

export async function markAllNotificationsAsRead(userId: string): Promise<void> {
  try {
    const token = localStorage.getItem('accessToken');
    await api.post(`api/notifications/${userId}/read-all`, {
      headers: {
        "Authorization": token ? `Bearer ${token}` : undefined,
        "Content-Type": "application/json",
      },
    });
  } catch (error) {
    console.error('Failed to mark all notifications as read:', error);
    // Silently fail - not critical
  }
}